import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sessionsApi } from '../services/api';
import './Sessions.css';

interface Session {
    id: number;
    url: string;
    name: string;
    scan_reason: string;
    created_at: string;
    scan_count: string;
    last_scan_at: string;
}

export default function Sessions() {
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        loadSessions();
    }, []);

    const loadSessions = async () => {
        try {
            const data = await sessionsApi.list();
            setSessions(data);
            setLoading(false);
        } catch (err) {
            setError('Failed to load sessions');
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Delete this session and all its scans?')) return;

        try {
            await sessionsApi.delete(id);
            setSessions(sessions.filter(s => s.id !== id));
        } catch (err) {
            alert('Failed to delete session');
        }
    };

    const formatUrl = (url: string) => {
        try {
            const parsed = new URL(url);
            return parsed.hostname;
        } catch {
            return url;
        }
    };

    if (loading) {
        return (
            <div className="sessions-loading">
                <div className="spinner-lg"></div>
            </div>
        );
    }

    return (
        <div className="sessions">
            <header className="sessions-header">
                <div>
                    <h1>Sessions</h1>
                    <p className="text-secondary">View and manage your debugging sessions</p>
                </div>
                <Link to="/" className="btn btn-primary">
                    + New Scan
                </Link>
            </header>

            {error && <div className="error-banner">{error}</div>}

            {sessions.length === 0 ? (
                <div className="empty-state">
                    <span className="empty-icon">--</span>
                    <h3>No sessions yet</h3>
                    <p className="text-secondary">Start a scan to create your first debugging session</p>
                    <Link to="/" className="btn btn-primary mt-md">
                        Start Scanning
                    </Link>
                </div>
            ) : (
                <div className="sessions-list">
                    {sessions.map((session) => (
                        <div key={session.id} className="session-card">
                            <div className="session-main">
                                <div className="session-info">
                                    <h3>{session.name || 'Untitled Session'}</h3>
                                    <p className="session-url">{formatUrl(session.url)}</p>
                                    {session.scan_reason && (
                                        <span className="session-reason">{session.scan_reason.replace(/_/g, ' ')}</span>
                                    )}
                                </div>
                                <div className="session-meta">
                                    <span className="scan-count">{session.scan_count} scans</span>
                                    <span className="last-scan">
                                        Last: {session.last_scan_at
                                            ? new Date(session.last_scan_at).toLocaleDateString()
                                            : 'Never'}
                                    </span>
                                </div>
                            </div>
                            <div className="session-actions">
                                <Link to={`/session/${session.id}`} className="btn btn-secondary">
                                    View Details
                                </Link>
                                <button
                                    className="btn-delete"
                                    onClick={() => handleDelete(session.id)}
                                    title="Delete session"
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
